import { z } from "zod";
import apiClient from "../../../shared/api/client";
import type {
  Design,
  ListDesignsQuery,
  ListDesignsResponse,
  ModerateDesignBody,
} from "../types";

const widgetTypeSchema = z.enum([
  "timer",
  "countdown",
  "clock",
  "chat",
  "playlist",
  "social",
  "subgoal",
  "followergoal",
  "viewers",
  "progressbar",
  "crawl",
]);

const statusSchema = z.enum(["draft", "pending", "approved", "rejected"]);
const licenseSchema = z.enum(["free", "paid"]);

const designSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  subtitle: z.string().nullable().optional(),
  widgetType: widgetTypeSchema,
  category: z.string(),
  description: z.string().nullable().optional(),
  // The SVG payload is sanitized server-side; kept opaque here and cast to the
  // SerializableDesign type the renderer consumes.
  design: z.unknown(),
  swatches: z.array(z.string()).default([]),
  fonts: z.array(z.string()).default([]),
  cardWidth: z.number(),
  cardHeight: z.number(),
  tags: z.array(z.string()).default([]),
  status: statusSchema,
  authorId: z.string().nullable().optional(),
  license: licenseSchema,
  priceArs: z.number().nullable().optional(),
  installCount: z.number().default(0),
  version: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const listDesignsResponseSchema = z.object({
  data: z.array(designSchema),
  total: z.number(),
  page: z.number().default(1),
  limit: z.number().default(20),
});

export const listDesigns = async (
  query: ListDesignsQuery = {},
): Promise<ListDesignsResponse> => {
  const response = await apiClient.get("/admin/designs", { params: query });
  const parsed = listDesignsResponseSchema.parse(response.data);
  return parsed as ListDesignsResponse;
};

export const moderateDesign = async (
  id: string,
  body: ModerateDesignBody,
): Promise<Design> => {
  const response = await apiClient.post(`/admin/designs/${id}/moderate`, body);
  return designSchema.parse(response.data) as Design;
};

export const designModerationApi = {
  listDesigns,
  moderateDesign,
};
